// React
import React from 'react';
import PropTypes from 'prop-types';
// Locales
import translate from 'locales/i18n';
// Material UI
import { withStyles } from '@mui/styles';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import SettingsIcon from '@mui/icons-material/Settings';
import DialogManageAccounts from './DialogManageAccounts';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
// Theme
import UserAction from 'actions/UserAction';

class DialogManageUser extends React.PureComponent {

    getInitialState = () => ({
        firstName: '',
        lastName: '',
        login: '',
        language: 'fr',
        maxConnection: 1,
        pageSize: 10,
        accounts: [],
        errorMsg: '',
        dialogManageAccountsOpen: false,
        permissions: {
            SuperUser: false,
            SPEScrapingDataEditor: true,
            SPEScrapedDataEditor: false
        },
        isBlocked: false,
        id: null
    });

    state = this.getInitialState();

    static getDerivedStateFromProps(props, state) {
        if (props.user && props.user.id !== state.id) {
            return {
                firstName: props.user ? props.user.firstName : '',
                lastName: props.user ? props.user.lastName : '',
                login: props.user ? props.user.login : '',
                language: props.user ? props.user.settings.language : 'fr',
                maxConnection: props.user ? props.user.maxConnection : 1,
                pageSize: props.user ? props.user.settings.pageSize : 10,
                accounts: props.user ? props.user.accounts : [],
                permissions: props.user ? props.user.permissions : [],
                isBlocked: props.user ? props.user.isBlocked : false,
                id: props.user.id,
            };
        } return null;
    }

    static propTypes = {
        close: PropTypes.func,
        open: PropTypes.bool,
        user: PropTypes.object,
        classes: PropTypes.object,
        fullScreen: PropTypes.bool
    };

    validateLogin = login => {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(login).toLowerCase());
    };

    checkInfo = () => {
        const { firstName, lastName, login, language, maxConnection, pageSize } = this.state;

        if (!firstName) {
            this.setState({ errorMsg: translate.i18n('ERROR_MSG', { name: translate.i18n('FIRSTNAME') }) });
            return false;
        }

        if (!lastName) {
            this.setState({ errorMsg: translate.i18n('ERROR_MSG', { name: translate.i18n('LASTNAME') }) });
            return false;
        }

        if (!login && this.validateLogin(login)) {
            this.setState({ errorMsg: translate.i18n('ERROR_MSG', { name: translate.i18n('LOGIN') }) });
            return false;
        }

        if (language !== 'en' && language !== 'fr') {
            this.setState({ errorMsg: translate.i18n('ERROR_MSG', { name: translate.i18n('LANGUAGE') }) });
            return false;
        }

        if (maxConnection < 1) {
            this.setState({ errorMsg: translate.i18n('ERROR_MSG', { name: translate.i18n('MAX_CONNECTION') }) });
            return false;
        }

        if (pageSize > 50 || pageSize < 5) {
            this.setState({ errorMsg: translate.i18n('ERROR_MSG', { name: translate.i18n('PAGE_SIZE') }) });
            return false;
        }

        return true;
    };

    addUser = () => {
        const { firstName, lastName, login, language, pageSize, maxConnection, accounts, permissions, isBlocked } = this.state;

        if (this.checkInfo()) {
            UserAction.addUser({
                firstName,
                lastName,
                login,
                maxConnection: parseInt(maxConnection),
                accounts,
                settings: {
                    pageSize,
                    language
                },
                permissions,
                isBlocked
            });
            this.close();
        }
    };

    updateUser = () => {
        const { firstName, lastName, login, language, pageSize, maxConnection, accounts, permissions, isBlocked } = this.state;

        if (this.checkInfo()) {
            UserAction.updateUser({
                firstName,
                lastName,
                login,
                maxConnection: parseInt(maxConnection),
                accounts,
                settings: {
                    pageSize,
                    language
                },
                permissions,
                isBlocked
            }, this.props.user.id);
            this.close();
        }
    };

    close = () => {
        this.setState(this.getInitialState());
        this.props.close();
    };

    closeDialog = () => {
        this.setState({
            dialogManageAccountsOpen: false
        });
    };

    addAccount = (account) => {
        const { accounts } = this.state;
        accounts.push(account);

        this.setState({ accounts: accounts.slice(0) });
    };

    deleteAccount = (accountId) => {
        let { accounts } = this.state;
        accounts = accounts.filter(account => account.id !== accountId);

        this.setState({ accounts });
    };

    handleChange = name => event => {
        this.setState({
            [name]: event.target.value,
            errorMsg: ''
        });
    };

    handleChangePermissions = name => event => {
        const newPermissions = this.state.permissions;

        newPermissions[name] = event.target.checked;

        if (name === 'SuperUser' && !event.target.checked) {
            this.setState({ permissions: Object.assign({}, newPermissions), accounts: [] });
        } else {
            this.setState({ permissions: Object.assign({}, newPermissions) });
        }
    };

    renderDialogManageAccounts = () => <DialogManageAccounts
        close={this.closeDialog}
        open={this.state.dialogManageAccountsOpen}
        addAccount={this.addAccount}
        deleteAccount={this.deleteAccount}
        accountsSelected={this.state.accounts}
    />;

    render() {
        const { fullScreen, classes, user, open } = this.props;
        const { firstName, lastName, login, language, pageSize, maxConnection, errorMsg, accounts, permissions, isBlocked } = this.state;

        return (
            <Dialog
                fullScreen={fullScreen}
                open={open}
                onClose={this.close}
                disableRestoreFocus={true}
                aria-labelledby='responsive-dialog-title'
                classes={{
                    paper: classes.dialogPaper
                }}
            >
                <IconButton color='secondary' onClick={this.close} className={classes.closeButton}>
                    <ClearIcon />
                </IconButton>
                <DialogTitle id='responsive-dialog-title' className={classes.dialogTitle}>{user ? translate.i18n('EDIT_USER') : translate.i18n('ADD_USER')}</DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <TextField
                        required
                        label={translate.i18n('FIRSTNAME')}
                        value={firstName}
                        variant='outlined'
                        className={classes.textField}
                        onChange={this.handleChange('firstName')}
                    />
                    <TextField
                        required
                        label={translate.i18n('LASTNAME')}
                        value={lastName}
                        variant='outlined'
                        className={classes.textField}
                        onChange={this.handleChange('lastName')}
                    />
                    <TextField
                        required
                        label={translate.i18n('LOGIN')}
                        value={login}
                        variant='outlined'
                        className={classes.textField}
                        onChange={this.handleChange('login')}
                    />
                    <div className={classes.settingsContainer}>
                        <FormControl variant='outlined'
                            classes={{
                                root: classes.formControl
                            }}>
                            <InputLabel
                                htmlFor='outlined'
                                margin={'dense'}>
                                {translate.i18n('LANGUAGE')}
                            </InputLabel>
                            <Select
                                value={language}
                                onChange={this.handleChange('language')}
                                label={translate.i18n('LANGUAGE')}
                            >
                                <MenuItem value={'fr'}>FR</MenuItem>
                                <MenuItem value={'en'}>EN</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl variant='outlined'
                            classes={{
                                root: classes.formControl
                            }}>
                            <InputLabel
                                htmlFor='outlined'
                                margin={'dense'}>
                                {translate.i18n('PAGE_SIZE')}
                            </InputLabel>
                            <Select
                                value={pageSize}
                                onChange={this.handleChange('pageSize')}
                                label={translate.i18n('PAGE_SIZE')}
                            >
                                <MenuItem value={5}>5</MenuItem>
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                    <TextField
                        required
                        label={translate.i18n('MAX_CONNECTION')}
                        type='number'
                        value={maxConnection}
                        variant='outlined'
                        className={classes.textField}
                        onChange={this.handleChange('maxConnection')}
                    />
                    {errorMsg ?
                        <p className={classes.errorMsg}>{errorMsg}</p>
                        :
                        null
                    }
                    <FormControlLabel
                        control={
                            <Switch
                                checked={isBlocked}
                                onChange={event => this.setState({ isBlocked: event.target.checked })}
                                name='Is blocked'
                                color='primary'
                            />
                        }
                        label={translate.i18n('USER_BLOCKED')}
                    />
                    <Button
                        variant='contained'
                        color='primary'
                        className={classes.button}
                        onClick={() => this.setState({ dialogManageAccountsOpen: true })}>
                        <SettingsIcon className={classes.leftIcon} />
                        {translate.i18n('ACCOUNTS') + ' (' + accounts.length + ')'}
                    </Button>
                    <p className={classes.rightsLabel}>{translate.i18n('RIGHTS')}</p>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={permissions.SuperUser}
                                onChange={this.handleChangePermissions('SuperUser')}
                                name='SuperUser'
                                color='primary'
                            />
                        }
                        label={translate.i18n('SUPER_USER')}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={permissions.SPEScrapingDataEditor}
                                onChange={this.handleChangePermissions('SPEScrapingDataEditor')}
                                name='SPEScrapingDataEditor'
                                color='primary'
                            />
                        }
                        label={translate.i18n('SCRAPING_DATA_EDITOR')}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={permissions.SPEScrapedDataEditor}
                                onChange={this.handleChangePermissions('SPEScrapedDataEditor')}
                                name='SPEScrapedDataEditor'
                                color='primary'
                            />
                        }
                        label={translate.i18n('SCRAPED_DATA_EDITOR')}
                    />
                </DialogContent>
                <DialogActions>
                    <Button color='secondary'
                        onClick={this.close}>
                        <ClearIcon className={classes.leftIcon} />
                        <div>{translate.i18n('CANCEL')}</div>
                    </Button>
                    <Button color='primary'
                        onClick={user ? this.updateUser : this.addUser}>
                        <AddIcon className={classes.leftIcon} />
                        <div>{user ? translate.i18n('SAVE') : translate.i18n('ADD_USER')}</div>
                    </Button>
                </DialogActions>
                {this.renderDialogManageAccounts()}
            </Dialog>
        );
    }
}

const styles = theme => ({
    dialogContent: {
        overflow: 'hidden',
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 0,
        minWidth: '400px'
    },
    dialogPaper: {
        minHeight: '50%',
        minWidth: '25%'
    },
    dialogTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingBottom: '5px'
    },
    settingsContainer: {
        marginTop: '16px',
        width: '80%',
        display: 'flex',
        justifyContent: 'space-between'
    },
    textField: {
        marginTop: '16px',
        width: '80%'
    },
    formControl: {
        width: '45%',
    },
    button: {
        marginTop: '4px',
        width: '80%'
    },
    closeButton: {
        position: 'absolute',
        right: '2px',
        top: '2px'
    },
    errorMsg: {
        fontSize: '12px',
        marginBottom: '0',
        marginTop: '8px',
        fontFamily: 'roboto',
        fontStyle: 'italic',
        color: theme.palette.red.main
    },
    leftIcon: {
        marginRight: '8px',
        fontSize: 20
    },
    rightsLabel: {
        marginBottom: '4px',
        borderBottom: 'solid 1px black'
    }
});

export default withStyles(styles)(DialogManageUser);
